import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import qs from "query-string";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import Container from "@material-ui/core/Container";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import { openApi } from "../../services/api";
import logo from "../../assets/logo.png";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #1d4ed8 0%, #7c3aed 100%)",
    padding: theme.spacing(2)
  },
  card: {
    maxWidth: 420,
    width: "100%",
    borderRadius: 16,
    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
    padding: theme.spacing(1)
  },
  logo: {
    display: "block",
    margin: "0 auto 24px",
    maxHeight: 56,
    objectFit: "contain"
  },
  title: {
    fontWeight: 700,
    fontSize: 22,
    textAlign: "center",
    marginBottom: 8,
    color: "#1e293b"
  },
  subtitle: {
    textAlign: "center",
    color: "#64748b",
    fontSize: 14,
    marginBottom: 24
  },
  emailHighlight: {
    fontWeight: 600,
    color: "#1d4ed8"
  },
  codeInput: {
    "& input": {
      textAlign: "center",
      fontSize: 28,
      letterSpacing: 12,
      fontWeight: 700
    }
  },
  submitBtn: {
    marginTop: 16,
    height: 48,
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 16
  },
  resendRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
    gap: 8
  },
  resendText: {
    color: "#64748b",
    fontSize: 13
  },
  resendBtn: {
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "none",
    padding: "0 4px",
    minWidth: 0,
    "&:disabled": {
      color: "#94a3b8"
    }
  },
  timer: {
    color: "#94a3b8",
    fontSize: 13
  }
}));

const VerificarEmail = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const { email } = qs.parse(location.search);

  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(60);

  useEffect(() => {
    if (!email) {
      history.replace("/login");
    }
  }, [email, history]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  const handleVerify = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      toast.warning("Digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    try {
      await openApi.post("/auth/verify-email", { email, code });
      toast.success("E-mail verificado com sucesso! Faça login para acessar.");
      history.replace("/login");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await openApi.post("/auth/resend-verification", { email });
      toast.info("Novo código enviado para " + email);
      setCountdown(60);
    } catch (err) {
      toastError(err);
    } finally {
      setResendLoading(false);
    }
  };

  const handleCodeChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(val);
  };

  return (
    <div className={classes.root}>
      <CssBaseline />
      <Container maxWidth="sm" style={{ padding: 0 }}>
        <Card className={classes.card}>
          <CardContent style={{ padding: "32px" }}>
            <img src={logo} alt="Logo" className={classes.logo} />

            <Typography className={classes.title}>
              Verifique seu e-mail
            </Typography>

            <Typography className={classes.subtitle}>
              Enviamos um código de 6 dígitos para{" "}
              <span className={classes.emailHighlight}>{email}</span>.<br />
              Digite-o abaixo para ativar sua conta.
            </Typography>

            <form onSubmit={handleVerify} noValidate>
              <TextField
                variant="outlined"
                fullWidth
                label="Código de verificação"
                value={code}
                onChange={handleCodeChange}
                inputProps={{ maxLength: 6 }}
                className={classes.codeInput}
                placeholder="000000"
                autoFocus
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                color="primary"
                className={classes.submitBtn}
                disabled={loading || code.length !== 6}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : "Verificar e-mail"}
              </Button>
            </form>

            <div className={classes.resendRow}>
              <Typography className={classes.resendText}>
                Não recebeu o código?
              </Typography>
              {countdown > 0 ? (
                <Typography className={classes.timer}>
                  Reenviar em {countdown}s
                </Typography>
              ) : (
                <Button
                  className={classes.resendBtn}
                  onClick={handleResend}
                  disabled={resendLoading}
                  disableRipple
                >
                  {resendLoading ? "Enviando..." : "Reenviar código"}
                </Button>
              )}
            </div>

            <div style={{ textAlign: "center", marginTop: 16 }}>
              <Button
                style={{ color: "#64748b", fontSize: 12, textTransform: "none" }}
                onClick={() => history.push("/login")}
              >
                Voltar para o login
              </Button>
            </div>
          </CardContent>
        </Card>
      </Container>
    </div>
  );
};

export default VerificarEmail;
